import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../_helpers/axios_instance';

const initialState = {
  survey_questions: [],
  id: 0,
  fetchingSurvey: false,
  surveyFetchError: null,
  submittingSurvey: false,
  surveySubmitError: null,

  pulseCheckSurvey: {},
  fetchingPulseCheckSurvey: false,
  pulseCheckSurveyError: null,

  submittingPulseCheckSurvey: false,
  pulseCheckSurveySubmitError: null,
};

export const getSurvey = createAsyncThunk(
  'survey/getSurvey',
  async ({ credentials, survey_id = process.env.REACT_APP_BURNOUT_SURVEY }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/surveys/${survey_id}`);
      return response.data; // This will be the payload of the 'fulfilled' action
    } catch (error) {
      console.error("ERROR FETCHING SURVEY DATA", error.response || error.message);
      const errors = error.response?.data?.errors;
      return rejectWithValue(errors || error.message); // This will be the payload of the 'rejected' action
    }
  },
  {
    // Condition to prevent running if already fetching
    condition: (_, { getState }) => {
      const { surveys } = getState();
      if (surveys.fetchingSurvey) {
        return false;
      }
    },
  }
);

export const submitSurvey = createAsyncThunk(
  'survey/submitSurvey',
  async ({ provider_survey_id, responses }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/all_surveyresponse/`, {
        provider_survey_id: provider_survey_id,
        responses: responses
      });
      return response.data;
    } catch (error) {
      console.error("ERROR SUBMITTING SURVEY DATA", error.response || error.message);
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  }
);

export const getPulseCheckSurvey = createAsyncThunk(
  'survey/getPulseCheckSurvey',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/surveys/${process.env.REACT_APP_PULSE_CHECK_SURVEY_ID}`);
      return response.data;
    } catch (error) {
      console.error("ERROR FETCHING PULSE CHECK SURVEY", error.response || error.message);
      const errors = error.response?.data?.errors;
      return rejectWithValue(errors || error.message);
    }
  }
);

export const submitPulseCheckSurveyResponse = createAsyncThunk(
  'survey/submitPulseCheckSurveyResponse',
  async ({ credentials, survey_question_choice_id, survey_text_response, base_question_id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/pulse_check/${credentials.user.id}`, {
        survey_question_choice_id,
        base_question_id,
        survey_text_response
      });
      return response.data;
    } catch (error) {
      console.error("ERROR SUBMITTING PULSE CHECK RESPONSE", error.response || error.message);
      const errors = error.response?.data?.errors;
      return rejectWithValue(errors || error.message);
    }
  }
);


// 3. Create the Slice
const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    // You can add synchronous reducers here if needed, e.g., to reset state.
    // clearSurveyError: (state) => {
    //   state.surveyFetchError = null;
    // }
  },
  extraReducers: (builder) => {
    builder
    // Cases for getSurvey
    .addCase(getSurvey.pending, (state) => {
      state.fetchingSurvey = true;
      state.surveyFetchError = null;
    })
    .addCase(getSurvey.fulfilled, (state, action) => {
      state.fetchingSurvey = false;
      // The original reducer used Object.assign({}, state, {...action.data})
      // This is the modern equivalent, merging the payload into the state
      Object.assign(state, action.payload);
      state.surveyFetchError = null;
    })
    .addCase(getSurvey.rejected, (state, action) => {
      state.fetchingSurvey = false;
      state.surveyFetchError = action.payload;
    })

    // Cases for submitSurvey
    .addCase(submitSurvey.pending, (state) => {
      state.submittingSurvey = true;
      state.surveySubmitError = null;
    })
    .addCase(submitSurvey.fulfilled, (state) => {
      state.submittingSurvey = false;
      state.surveySubmitError = null;
    })
    .addCase(submitSurvey.rejected, (state, action) => {
      state.submittingSurvey = false;
      state.surveySubmitError = action.payload;
    })

    // Cases for getPulseCheckSurvey
    .addCase(getPulseCheckSurvey.pending, (state) => {
      state.fetchingPulseCheckSurvey = true;
      state.pulseCheckSurveyError = null;
    })
    .addCase(getPulseCheckSurvey.fulfilled, (state, action) => {
      state.fetchingPulseCheckSurvey = false;
      state.pulseCheckSurvey = action.payload;
    })
    .addCase(getPulseCheckSurvey.rejected, (state, action) => {
      state.fetchingPulseCheckSurvey = false;
      state.pulseCheckSurveyError = action.payload;
    })

    // Cases for submitPulseCheckSurveyResponse
    .addCase(submitPulseCheckSurveyResponse.pending, (state) => {
      state.submittingPulseCheckSurvey = true;
      state.pulseCheckSurveySubmitError = null;
    })
    .addCase(submitPulseCheckSurveyResponse.fulfilled, (state) => {
      // The old reducer didn't have a success case for this, but resetting the status is good practice
      state.submittingPulseCheckSurvey = false;
    })
    .addCase(submitPulseCheckSurveyResponse.rejected, (state, action) => {
      state.submittingPulseCheckSurvey = false;
      state.pulseCheckSurveySubmitError = action.payload;
    });
  },
});

export default surveySlice.reducer;

const questionSort = (q1, q2) => (q1.display_order - q2.display_order);

export const getAllQuestions = (surveyState) => surveyState.survey_questions.map(q => {
  const {id, question, type, instructions, short_question, question_num, category, choices, display_options, question_group} = q.question;
  return {id, question, type, instructions, short_question, question_num, category, choices, display_options, display_order: q.display_order, question_group};
}).sort(questionSort);

export const getMultipleChoiceQuestionsForCharts = (surveyState) => {
  const questions = getAllQuestions(surveyState);
  return questions.filter(q => q.type === "MultipleChoiceQuestion" && q.display_options?.show_on_chart === true);
};

export const getChoices = (surveyState, question) => {
  if (question && question.hasOwnProperty("choices")) {
    return question.choices;
  } else return [];
};

export function getBurnoutQuestion(surveyState) {
  const questions = getAllQuestions(surveyState);
  return questions.find(q => q.short_question === process.env.REACT_APP_BURNOUT_SHORT_QUESTION);
}

export function getAgreeDisagreeQuestions(surveyState) {
  const questions = getMultipleChoiceQuestionsForCharts(surveyState);
  return questions.filter(q => q.type === "MultipleChoiceQuestion"  && q.short_question !== process.env.REACT_APP_BURNOUT_SHORT_QUESTION);
}

export function getFreeTextQuestionsForCharts(surveyState) {
  const questions = getAllQuestions(surveyState);
  return questions.filter(q => q.type === "FreeTextQuestion" && q.display_options?.show_on_chart === true);
}

export function getDemographicQuestions(surveyState) {
  const questions = getAllQuestions(surveyState);
  return questions.filter(q => (q.category?.id === parseInt(process.env.REACT_APP_DEMOGRAPHIC_QUESTION_CATEGORY_ID) || q.category?.name === 'Demographic'));
}